import { Feature } from '@pubstack/common/src/rolesAndFeatures'
import { AdminScope } from '@pubstack/common/src/scope'
import { sortByName } from '@pubstack/common/src/sort'
import { useQueryClient } from '@tanstack/react-query'
import { buildUrlWithSearchParams } from '~/utils/url'
import { get, MutationToasts, useMutation, useQuery } from '../api-client'

export const ADMIN_SCOPES_QUERY_KEYS = ['admin', 'scopes']

//-------------------------------------------------------------------------
// Queries
//-------------------------------------------------------------------------
export const useAdminScope = (scopeId?: string) => {
  return useQuery<AdminScope>({ url: `/admin/scopes/${scopeId}`, keys: [...ADMIN_SCOPES_QUERY_KEYS, scopeId ?? ''], enabled: !!scopeId })
}

export const useAdminScopes = (enabled?: boolean) => {
  const url = buildUrlWithSearchParams('/admin/scopes', {
    enabled,
  })

  const queryResult = useQuery<AdminScope[]>({ url, keys: [...ADMIN_SCOPES_QUERY_KEYS, { enabled }], select: sortByName })

  return { ...queryResult, data: queryResult.data || [] }
}

// Prefetch the admin scopes list to warm the admin lambda (cold start) and populate the cache ahead
// of time, e.g. when the command palette opens, so the first real access is instant.
export const usePrefetchAdminScopes = () => {
  const queryClient = useQueryClient()
  return (enabled?: boolean) => {
    const url = buildUrlWithSearchParams('/admin/scopes', { enabled })
    return queryClient.prefetchQuery({ queryKey: [...ADMIN_SCOPES_QUERY_KEYS, { enabled }], queryFn: () => get<AdminScope[]>(url) })
  }
}

//-------------------------------------------------------------------------
// Mutations
//-------------------------------------------------------------------------
export const useCreateScope = (toasts: MutationToasts = {}) => {
  return useMutation<AdminScope>({
    method: 'post',
    url: '/admin/scopes',
    collectionKeys: ADMIN_SCOPES_QUERY_KEYS,
    ...toasts,
  })
}

export const useUpdateScope = (scopeId: string, toasts: MutationToasts = {}) => {
  return useMutation<AdminScope>({
    method: 'put',
    url: `/admin/scopes/${scopeId}`,
    collectionKeys: ADMIN_SCOPES_QUERY_KEYS,
    resourceId: scopeId,
    ...toasts,
  })
}

export const useUpdateScopeFeature = (scopeId: string, toasts: MutationToasts = {}) => {
  return useMutation<Feature[]>({
    method: 'put',
    url: `/admin/scopes/${scopeId}/features`,
    collectionKeys: ADMIN_SCOPES_QUERY_KEYS,
    resourceId: scopeId,
    ...toasts,
  })
}

export const useArchiveScope = (scopeId: string, toasts: MutationToasts = {}) => {
  return useMutation<AdminScope>({
    method: 'post',
    url: `/admin/scopes/${scopeId}/archive`,
    collectionKeys: ADMIN_SCOPES_QUERY_KEYS,
    resourceId: scopeId,
    ...toasts,
  })
}

export const useUnarchiveScope = (scopeId: string, toasts: MutationToasts = {}) => {
  return useMutation<AdminScope>({
    method: 'post',
    url: `/admin/scopes/${scopeId}/unarchive`,
    collectionKeys: ADMIN_SCOPES_QUERY_KEYS,
    resourceId: scopeId,
    ...toasts,
  })
}

export const useDeleteScope = (scopeId: string, toasts: MutationToasts = {}) => {
  return useMutation<void>({
    method: 'delete',
    url: `/admin/scopes/${scopeId}`,
    collectionKeys: ADMIN_SCOPES_QUERY_KEYS,
    resourceId: scopeId,
    ...toasts,
  })
}
